import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { FaRegSave } from "react-icons/fa";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import api from "../api/axios";
import { useAuth } from "../auth/AuthContext";
import OwnerInfo from "./OwnerInfo";
import "./AddContactModal.css";

// Zod validation schema with phone validation
const customerSchema = z.object({
  firstName: z.string()
    .min(1, "First name is required")
    .min(2, "First name must be at least 2 characters")
    .max(50, "First name cannot exceed 50 characters"),
  lastName: z.string()
    .min(1, "Last name is required")
    .min(2, "Last name must be at least 2 characters")
    .max(50, "Last name cannot exceed 50 characters"),
  email: z.string()
    .min(1, "Email is required")
    .email("Please enter a valid email address"),
  phone: z.string()
    .optional()
    .refine((value) => {
      if (!value) return true;
      const phoneNumber = parsePhoneNumberFromString(`+${value}`);
      return phoneNumber ? phoneNumber.isValid() : false;
    }, "Please enter a valid phone number"),
  jobTitle: z.string()
    .min(1, "Job title is required")
    .max(100, "Job title cannot exceed 100 characters"),
  company: z.string()
    .min(1, "Company is required"),
  status: z.string().optional(),
  source: z.string().optional(),
  linkedin: z.string()
    .refine((value) => !value || /^(https?:\/\/)?(www\.)?linkedin\.com/.test(value),
      "Please enter a valid LinkedIn URL"
    )
    .optional(),
  notes: z.string()
    .max(500, "Notes cannot exceed 500 characters")
    .optional(),
  collab_ids: z.array(z.string()).optional(),
});

const STATUS_OPTIONS = ["Active", "Qualified", "Inactive"];
const SOURCE_OPTIONS = ["Website", "Referral", "LinkedIn", "Event", "Other"];

const AddContactModal = ({ onClose, onSuccess }) => {
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [apiError, setApiError] = useState("");
  const { user } = useAuth();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    watch,
    reset,
  } = useForm({
    resolver: zodResolver(customerSchema),
    mode: "onChange",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      jobTitle: "",
      company: "",
      status: "Active",
      source: "Website",
      linkedin: "",
      notes: "",
      collab_ids: [],
    },
  });

  const formValues = watch();

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const res = await api.get("/api/companies/", { params: { limit: 500 } });
        setCompanies(res.data);
      } catch (err) {
        console.error("Failed to load companies", err);
      }
    };
    loadCompanies();
  }, []);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const res = await api.get('/api/admin/users/');
        setUsers(res.data || []);
      } catch (err) {}
    };
    loadUsers();
  }, []);

  const onSubmit = async (data) => {
    setApiError("");
    try {
      const payload = {
        name: `${data.firstName.trim()} ${data.lastName.trim()}`,
        email: data.email.trim(),
        phone_number: data.phone ? `+${data.phone}` : null,
        position: data.jobTitle.trim() || null,
        company_id: data.company,
        collab_ids: Array.isArray(data.collab_ids) ? data.collab_ids : (data.collab_ids ? [data.collab_ids] : []),
        status: data.status ? String(data.status).toLowerCase() : null,
        source: data.source ? String(data.source).toLowerCase() : null,
        linkedin: data.linkedin?.trim() || null,
        notes: data.notes?.trim() || null,
      };

      console.log("Creating customer payload:", payload);
      await api.post("/api/customers/", payload);
      if (onSuccess) onSuccess();
      reset();
    } catch (err) {
      console.error(err);
      setApiError("Failed to create contact. Please try again.");
    }
  };

  const renderFormField = (label, fieldName, placeholder, isTextarea = false, maxLength = 100, isSelect = false, options = [], isRequired = false) => {
    const hasError = !!errors[fieldName];
    const fieldValue = formValues[fieldName] || "";

    return (
      <div key={fieldName}>
        <label className={`form-label ${hasError ? "label-error" : ""}`}>
          {label}{isRequired && " *"}
        </label>
        {isSelect ? (
          <select {...register(fieldName)} className={`form-select ${hasError ? "input-error" : ""}`}>
            <option value="">{`Select ${label}`}</option>
            {options.map((option) =>
              typeof option === "object" ? (
                <option key={option.value} value={option.value}>{option.label}</option>
              ) : (
                <option key={option} value={option}>{option}</option>
              )
            )}
          </select>
        ) : isTextarea ? (
          <textarea
            {...register(fieldName)}
            placeholder={placeholder}
            rows={4}
            maxLength={maxLength}
            className={`form-textarea ${hasError ? "input-error" : ""}`}
          />
        ) : (
          <input
            {...register(fieldName)}
            type={fieldName === "email" ? "email" : fieldName === "linkedin" ? "url" : "text"}
            placeholder={placeholder}
            maxLength={maxLength}
            className={`form-input ${hasError ? "input-error" : ""}`}
          />
        )}
        {hasError && <div className="field-error">{errors[fieldName].message}</div>}
        {!isSelect && <div className="char-count">{fieldValue.length}/{maxLength}</div>}
      </div>
    );
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.35)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 }}>
      <div style={{ background: "#fff", borderRadius: "16px", width: "700px", maxHeight: "90vh", overflowY: "auto", padding: "2rem 2.5rem", boxShadow: "0 25px 50px rgba(15, 23, 42, 0.35)", position: "relative" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
          <h2 style={{ fontSize: "1.6rem", fontWeight: "700" }}>Add New Contact</h2>
          <button onClick={onClose} disabled={isSubmitting} style={{ background: "transparent", border: "none", fontSize: "1.3rem", cursor: isSubmitting ? "not-allowed" : "pointer", opacity: isSubmitting ? 0.6 : 1 }}>✕</button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <OwnerInfo user={user} />

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem 1.5rem", marginBottom: "1.5rem" }}>
            {renderFormField("First Name", "firstName", "John", false, 50, false, [], true)}
            {renderFormField("Last Name", "lastName", "Doe", false, 50, false, [], true)}
            {renderFormField("Email", "email", "john@example.com", false, 100, false, [], true)}

            {/* Phone input */}
            <div>
              <label className={`form-label ${errors.phone ? "label-error" : ""}`}>Phone</label>
              <Controller
                name="phone"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <PhoneInput
                    {...field}
                    country="us"
                    enableAreaCodes={false}
                    countryCodeEditable={false}
                    inputClass={`form-input ${errors.phone ? "input-error" : ""}`}
                    value={field.value}
                    onChange={field.onChange} // returns full number without "+"
                    inputStyle={{ height: "45.6px" }}
                  />
                )}
              />
              {errors.phone && <div className="field-error">{errors.phone.message}</div>}
            </div>

            {renderFormField("Job Title", "jobTitle", "Sales Manager", false, 100, false, [], true)}
            {renderFormField("Company", "company", "Select company", false, 100, true, companies.map((c) => ({ value: c.id, label: c.name })), true)}
            {renderFormField("Status", "status", "Select status", false, 100, true, STATUS_OPTIONS, false)}
            {renderFormField("Source", "source", "Select source", false, 100, true, SOURCE_OPTIONS, false)}
            {renderFormField("LinkedIn URL", "linkedin", "https://linkedin.com/in/...", false, 200, false, [], false)}

            <div>
              <label className="form-label">Collaborators</label>
              <select {...register("collab_ids")} className={`form-select`} multiple>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.full_name || u.username || u.email}</option>
                ))}
              </select>
              <small>Select one or more collaborators (Ctrl+Click or Cmd+Click)</small>
            </div>
          </div>

          <div style={{ marginBottom: "1.5rem" }}>
            {renderFormField("Notes", "notes", "Internal notes about this contact...", true, 500, false, [], false)}
          </div>

          {apiError && (
            <div style={{ background: "#fee2e2", border: "1px solid #fecaca", color: "#b91c1c", fontSize: "13px", marginBottom: "1rem", padding: "12px", borderRadius: "8px" }}>
              ⚠ {apiError}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "1rem" }}>
            <button type="button" onClick={onClose} disabled={isSubmitting} style={{ padding: "10px 18px", borderRadius: "8px", border: "1px solid #d1d5db", background: "#fff", cursor: isSubmitting ? "not-allowed" : "pointer", minWidth: "100px", opacity: isSubmitting ? 0.6 : 1 }}>Cancel</button>
            <button type="submit" disabled={isSubmitting || Object.keys(errors).length > 0} style={{ padding: "10px 18px", borderRadius: "8px", border: "none", background: Object.keys(errors).length > 0 ? "#9ca3af" : "#2563eb", color: "#fff", fontWeight: "600", cursor: Object.keys(errors).length > 0 || isSubmitting ? "not-allowed" : "pointer", minWidth: "130px", opacity: isSubmitting ? 0.7 : 1, display: "flex", alignItems: "center", gap: "8px", justifyContent: "center" }}>
              <FaRegSave /> {isSubmitting ? "Saving..." : "Save Contact"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddContactModal;
